import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Alert,
  Skeleton,
  CircularProgress,
  Button
} from '@mui/material';
import { getComments, createComment } from '../../services/api';
import { Comment } from '../../types/Comment';
import { useAuth } from '../../context/AuthContext';
import CommentItem from './CommentItem';

interface CommentsProps {
  postId: string;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
}

const Comments: React.FC<CommentsProps> = ({ postId }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const { user, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [newComment, setNewComment] = useState('');
  const initialLimit = 10;
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    page: 1,
    limit: initialLimit
  });

  // Fetch comments in nested structure
  const fetchComments = async (pageNum: number, limit: number) => {
    try {
      const { data }: any = await getComments(postId, pageNum, limit);
      // If backend supports nested, use as is
      const formattedComments = (data.comments as any[]).map((c) => ({
        ...c,
        id: c.id || c._id
      }));
      if (pageNum === 1) {
        setComments(formattedComments);
      } else {
        setComments(prev => [...prev, ...formattedComments]);
      }
      setPagination({
        total: data.pagination.total,
        page: pageNum,
        limit: data.pagination.limit
      });
    } catch (error) {
      setError((error as any).message);
    }
  };

  useEffect(() => {
    const loadInitialComments = async () => {
      setIsLoading(true);
      await fetchComments(1, initialLimit);
      setIsLoading(false);
    };
    loadInitialComments();
    // eslint-disable-next-line
  }, [postId]);

  const handleLoadMore = async () => {
    if (comments.length >= pagination.total) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    await fetchComments(nextPage, initialLimit);
    setPage(nextPage);
    setIsLoadingMore(false);
  };

  // Add a new top-level comment
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setError(null);
    try {
      setIsLoading(true);
      const dataToSend: any = {
        firstName: user?.firstName,
        lastName: user?.lastName,
        userId: user?.id,
        postId,
        content: newComment,
        imageUrl: user?.imageUrl
      };
      const { data } = await createComment(dataToSend);
      data.id = data._id as any;
      setComments([data, ...comments]);
      setNewComment('');
      setPagination(prev => ({
        ...prev,
        total: prev.total + 1
      }));
    } catch (err: any) {
      if(err?.response?.data?.errors) {
        err.response.data.errors.forEach((error: any) => {
          setError(error.msg || 'Failed to post comment');
        });
      } else {
        setError(err.response.data.message || 'Failed to post comment');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Add a reply to a comment (recursive)
  const handleReplyAdded = (reply: Comment, parentId: string) => {
    const addReply = (items: Comment[]): Comment[] =>
      items.map(item => {
        if (item.id === parentId || item._id === parentId) {
          return {
            ...item,
            replies: item.replies ? [reply, ...item.replies] : [reply]
          };
        } else if (item.replies && item.replies.length > 0) {
          return {
            ...item,
            replies: addReply(item.replies)
          };
        }
        return item;
      });
    setComments(prev => addReply(prev));
    // Don't update pagination total for replies - only for top-level comments
  };

  // Update likes/dislikes for a comment (recursive)
  const handleLikeDislike = (commentId: string, updated: Partial<Comment>) => {
    const updateComment = (items: Comment[]): Comment[] =>
      items.map(item => {
        if (item.id === commentId || item._id === commentId) {
          return { ...item, ...updated };
        } else if (item.replies && item.replies.length > 0) {
          return {
            ...item,
            replies: updateComment(item.replies)
          };
        }
        return item;
      });
    setComments(prev => updateComment(prev));
  };

  // Delete a comment (recursive)
  const handleCommentDeleted = (commentId: string) => {
    const removeComment = (items: Comment[]): Comment[] => {
      const filtered = items.filter(item => {
        if (item.id === commentId || item._id === commentId) {
          // Check if this is a top-level comment (no parentId)
          const isTopLevel = !item.parentId;
          if (isTopLevel) {
            // Only decrement total for top-level comments
            setPagination(prev => ({ ...prev, total: prev.total - 1 }));
          }
          return false; // Remove this comment
        } else if (item.replies && item.replies.length > 0) {
          item.replies = removeComment(item.replies);
        }
        return true;
      });
      return filtered;
    };
    setComments(prev => removeComment(prev));
  };

  const hasMoreComments = comments.length < pagination.total;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Comments ({pagination.total})
      </Typography>
      {isAuthenticated && (
        <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: 'background.paper' }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <textarea
                style={{ width: '100%', minHeight: 60, resize: 'vertical', padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                placeholder="Write a comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                required
              />
              {error && (
                <Alert severity="error" sx={{ width: '100%' }}>
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                sx={{ alignSelf: 'flex-end', minWidth: 120 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Post Comment'}
              </Button>
            </Stack>
          </form>
        </Paper>
      )}
      {isLoading && page === 1 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Stack spacing={1} alignItems="center">
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width={100} height={20} />
          </Stack>
        </Box>
      ) : (
        <Stack spacing={2}>
          {comments.map((comment) => (
            <CommentItem
              key={comment.id || comment._id}
              comment={comment}
              onReplyAdded={handleReplyAdded}
              onLikeDislike={handleLikeDislike}
              onCommentDeleted={handleCommentDeleted}
              replies={comment.replies || []}
              depth={0}
            />
          ))}
        </Stack>
      )}
      {hasMoreComments && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            sx={{ minWidth: '120px' }}
          >
            {isLoadingMore ? (
              <Skeleton variant="text" width={80} height={24} />
            ) : (
              `See More (${pagination.total - comments.length} remaining)`
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Comments; 